import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
import _taggedTemplateLiteral from "@babel/runtime/helpers/esm/taggedTemplateLiteral";
import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
var _excluded = ["renderIndicator"],
    _excluded2 = ["renderLabel"],
    _excluded3 = ["checked", "dataTestId", "hideLabel", "id", "indeterminate", "isDisabled", "label", "labelPosition", "name", "onChange", "renderIndicator", "renderLabel", "size", "truncateLabel"];

var _CHECKBOX_WRAPPER_SIZ, _templateObject, _templateObject2, _templateObject3, _templateObject4, _templateObject5;

function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);

  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    enumerableOnly && (symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    })), keys.push.apply(keys, symbols);
  }

  return keys;
}

function _objectSpread(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = null != arguments[i] ? arguments[i] : {};
    i % 2 ? ownKeys(Object(source), !0).forEach(function (key) {
      _defineProperty(target, key, source[key]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) {
      Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
    });
  }

  return target;
}

import React, { cloneElement, useEffect } from 'react';
import { useShareForwardedRef } from "../../utilities";
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { CHECKBOX_LABEL_POSITIONS, CHECKBOX_SIZES } from "../constants";
import { BREAKPOINTS, getRem, visuallyHidden } from "./../../core";
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
var CHECKBOX_WRAPPER_SIZES = (_CHECKBOX_WRAPPER_SIZ = {}, _defineProperty(_CHECKBOX_WRAPPER_SIZ, CHECKBOX_SIZES.LARGE, '48px'), _defineProperty(_CHECKBOX_WRAPPER_SIZ, CHECKBOX_SIZES.SMALL, '32px'), _defineProperty(_CHECKBOX_WRAPPER_SIZ, CHECKBOX_SIZES.STANDARD, '40px'), _CHECKBOX_WRAPPER_SIZ);
var beforeLabelStyles = css(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n  padding-right: ", ";\n"])), getRem('15px'));
var StyledCheckboxWrapper = styled.label(_templateObject2 || (_templateObject2 = _taggedTemplateLiteral(["\n  align-items: center;\n  box-sizing: border-box;\n  cursor: ", ";\n  display: inline-flex;\n  min-height: ", ";\n  min-width: 0;\n  width: 100%;\n\n  @media ", " {\n    min-height: ", ";\n    padding-right: ", ";\n    width: auto;\n  }\n"])), function (_ref) {
  var isDisabled = _ref.isDisabled;
  return isDisabled ? 'not-allowed' : 'pointer';
}, getRem(CHECKBOX_WRAPPER_SIZES[CHECKBOX_SIZES.LARGE]), BREAKPOINTS.L, function (_ref2) {
  var size = _ref2.size;
  return getRem(CHECKBOX_WRAPPER_SIZES[size]);
}, function (_ref3) {
  var hideLabel = _ref3.hideLabel;
  return !hideLabel && getRem('32px');
});
var StyledCheckboxInput = styled.input(_templateObject3 || (_templateObject3 = _taggedTemplateLiteral(["\n  ", "\n"])), visuallyHidden);
var StyledCheckboxIndicator = styled(function (_ref4) {
  var renderIndicator = _ref4.renderIndicator,
      props = _objectWithoutProperties(_ref4, _excluded);

  return /*#__PURE__*/cloneElement(renderIndicator(), props);
})(_templateObject4 || (_templateObject4 = _taggedTemplateLiteral(["\n  order: ", ";\n\n  && {\n    margin: ", ";\n\n    @media ", " {\n      margin: ", ";\n    }\n  }\n"])), function (_ref5) {
  var labelPosition = _ref5.labelPosition;
  return labelPosition === CHECKBOX_LABEL_POSITIONS.BEFORE ? 1 : 0;
}, function (_ref6) {
  var hideLabel = _ref6.hideLabel,
      labelPosition = _ref6.labelPosition,
      theme = _ref6.theme;
  return labelPosition === CHECKBOX_LABEL_POSITIONS.BEFORE && !hideLabel && "0 ".concat(theme.size.spacing.medium.value);
}, BREAKPOINTS.L, function (_ref7) {
  var hideLabel = _ref7.hideLabel,
      labelPosition = _ref7.labelPosition;
  return labelPosition === CHECKBOX_LABEL_POSITIONS.BEFORE && !hideLabel && 0;
});
var StyledCheckboxLabel = styled(function (_ref8) {
  var renderLabel = _ref8.renderLabel,
      props = _objectWithoutProperties(_ref8, _excluded2);

  return /*#__PURE__*/cloneElement(renderLabel(), props);
})(_templateObject5 || (_templateObject5 = _taggedTemplateLiteral(["\n  order: ", ";\n\n  && {\n    ", ";\n  }\n"])), function (_ref9) {
  var labelPosition = _ref9.labelPosition;
  return labelPosition === CHECKBOX_LABEL_POSITIONS.BEFORE ? 0 : 1;
}, function (_ref10) {
  var labelPosition = _ref10.labelPosition;
  return labelPosition === CHECKBOX_LABEL_POSITIONS.BEFORE && beforeLabelStyles;
});
var BaseCheckbox = /*#__PURE__*/React.forwardRef(function (_ref11, ref) {
  var checked = _ref11.checked,
      dataTestId = _ref11.dataTestId,
      hideLabel = _ref11.hideLabel,
      id = _ref11.id,
      indeterminate = _ref11.indeterminate,
      isDisabled = _ref11.isDisabled,
      label = _ref11.label,
      labelPosition = _ref11.labelPosition,
      name = _ref11.name,
      onChange = _ref11.onChange,
      renderIndicator = _ref11.renderIndicator,
      renderLabel = _ref11.renderLabel,
      size = _ref11.size,
      truncateLabel = _ref11.truncateLabel,
      other = _objectWithoutProperties(_ref11, _excluded3);

  var checkboxInputRef = useShareForwardedRef(ref);
  useEffect(function () {
    checkboxInputRef.current.indeterminate = indeterminate;
  }, [checkboxInputRef, indeterminate]);
  return /*#__PURE__*/_jsxs(StyledCheckboxWrapper, _objectSpread(_objectSpread({
    "data-testid": dataTestId,
    hideLabel: hideLabel || !label,
    htmlFor: id || name,
    isDisabled: isDisabled,
    size: size,
    truncateLabel: truncateLabel
  }, other), {}, {
    children: [/*#__PURE__*/_jsx(StyledCheckboxInput, {
      checked: checked,
      "data-testid": dataTestId ? "".concat(dataTestId, "-input") : undefined,
      disabled: isDisabled,
      id: id || name,
      name: name,
      onChange: onChange,
      ref: checkboxInputRef,
      tabIndex: -1,
      type: "checkbox"
    }), /*#__PURE__*/_jsx(StyledCheckboxIndicator, {
      checked: checked,
      "data-testid": dataTestId ? "".concat(dataTestId, "-indicator") : undefined,
      hideLabel: hideLabel,
      indeterminate: indeterminate,
      isDisabled: isDisabled,
      labelPosition: labelPosition,
      renderIndicator: renderIndicator,
      size: size
    }), !!label && /*#__PURE__*/_jsx(StyledCheckboxLabel, {
      "data-testid": dataTestId ? "".concat(dataTestId, "-label") : undefined,
      hideLabel: hideLabel,
      label: label,
      labelPosition: labelPosition,
      renderLabel: renderLabel,
      size: size,
      truncateLabel: truncateLabel
    })]
  }));
});
BaseCheckbox.propTypes = {
  /** The current value of checkbox - checked or unchecked */
  checked: PropTypes.bool.isRequired,

  /** Provides test id for testing libraries */
  dataTestId: PropTypes.string,

  /** Visually hides checkbox label */
  hideLabel: PropTypes.bool,

  /** Identifier for this checkbox */
  id: PropTypes.string,

  /** Changes the icon and sets checkbox indeterminate parameter state */
  indeterminate: PropTypes.bool,

  /** Disables to change the value of checkbox and shows it visually disabled */
  isDisabled: PropTypes.bool,

  /** Checkbox label */
  label: PropTypes.node,

  /** Places label before or after indicator */
  labelPosition: PropTypes.oneOf(Object.values(CHECKBOX_LABEL_POSITIONS)),

  /** Name of the checkbox */
  name: PropTypes.string.isRequired,

  /** Callback that is called when interacting with checkbox */
  onChange: PropTypes.func.isRequired,

  /** Renders custom element inside a component. It should represent the default checkbox indicator */
  renderIndicator: PropTypes.func.isRequired,

  /** Renders custom element inside a component. Should be used for providing label appearance */
  renderLabel: PropTypes.func.isRequired,

  /** Checkbox size */
  size: PropTypes.oneOf(Object.values(CHECKBOX_SIZES)),

  /** If true, truncates the checkbox label */
  truncateLabel: PropTypes.bool
};
BaseCheckbox.defaultProps = {
  dataTestId: undefined,
  hideLabel: false,
  id: '',
  indeterminate: false,
  isDisabled: false,
  label: '',
  labelPosition: CHECKBOX_LABEL_POSITIONS.AFTER,
  size: CHECKBOX_SIZES.STANDARD,
  truncateLabel: false
};
export { BaseCheckbox, StyledCheckboxWrapper };